import React, { useEffect, useState } from 'react';
import {
  Loader2,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Clock,
  FileDown,
  Info,
  FlaskConical,
} from 'lucide-react';
import { billingAPI } from '../../services/api';
import type { InvoiceLedgerEntry, InvoiceLedgerStatus } from '../../types/billing';
import { useBillingRefreshSource } from '../../contexts/BillingContext';
import { formatServerDate } from '../../utils/datetime';

/**
 * Shared invoice-history section — ONE component, both billing surfaces.
 *
 * Lived inline in `pages/BillingPage.tsx` until 2026-08-19, when the Shopify
 * billing view needed the same history and the alternative was a second
 * implementation that would drift. `GET /api/billing/workspace/{id}/invoices`
 * already serves Shopify workspaces (`back/api/billing.py` sets
 * `provider = "shopify"` and skips the live-Stripe layer entirely), so the only
 * provider-specific work here is copy, not fetching.
 *
 * 🚨 Provider-aware copy, because the two providers are NOT the same promise:
 * for Stripe we issue the invoice and can hand over a PDF; for Shopify the
 * merchant is billed by Shopify, the charge lives on THEIR admin charges page,
 * and we never issue an invoice document. The row link and the footnote both
 * have to say that rather than implying a download that does not exist.
 */

const formatDate = (iso?: string | null) =>
  formatServerDate(iso, undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const formatCurrency = (amountCents: number, currency = 'usd') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
};


// ---------- invoice ledger (Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08 §6) ----------
//
// The flagged replacement for the legacy `billing.invoices` table. The whole
// reason it exists is that the old section could not tell "this workspace has
// never been charged" apart from "the Stripe call blew up and we caught the
// exception" — both rendered the words "No invoices yet." So this component
// keeps FOUR states apart and never collapses them into two:
//
//   loading                                   → skeleton
//   entries [] + source_degraded false        → "No invoices yet."
//   entries [] + source_degraded true         → ⚠️ we couldn't load it (NEVER "no invoices")
//   entries non-empty + source_degraded true  → the table PLUS "may be incomplete"
//
// A hard request failure (network / 404 because the backend gate is off while
// the session flag says on) is a fifth, distinct state with a Retry — it is
// also never "No invoices yet."

const PROVIDER_LABEL: Record<string, string> = {
  stripe: 'Stripe',
  shopify: 'Shopify',
  bundle: 'AskProdigy',
  internal: 'AskProdigy',
};

const STATUS_CHIP: Record<InvoiceLedgerStatus, { label: string; className: string }> = {
  // 🚨 `upcoming` is not money that has moved. It gets its own blue/indigo
  // chip + clock icon so it can never read as `paid` at a glance.
  upcoming: {
    label: 'Upcoming',
    className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  },
  paid: {
    label: 'Paid',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  open: {
    label: 'Open',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  void: {
    label: 'Void',
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
};

// Statuses where the money never left the customer's account. `refunded` is
// deliberately NOT here — that money did move, and then came back.
const NOT_CHARGED_STATUSES: InvoiceLedgerStatus[] = ['upcoming', 'failed', 'void'];

// 🚨 `simulated` is written by the admin cycle simulator, which synthesizes the
// renewal payload LOCALLY and never calls Shopify. So the row says `paid` and
// carries an amount, and NO MONEY MOVED — the strongest possible reason to
// treat it like the not-charged statuses rather than like revenue. A badge
// alone would leave "$99.00" styled exactly like a real payment two rows down.
//
// ⚠️ `=== true`, never truthiness: absent means REAL. Mislabelling a genuine
// payment as a test artifact is the worse error — it invites someone to
// dismiss money that actually moved.
const isSimulated = (entry: InvoiceLedgerEntry) => entry.simulated === true;
const noMoneyMoved = (entry: InvoiceLedgerEntry) =>
  isSimulated(entry) || NOT_CHARGED_STATUSES.includes(entry.status);

// Small, unmistakable, and never the same colour as any status chip — the
// reader must not have to compare it against `Paid` to work out which one
// wins. It sits BESIDE the status rather than replacing it, because the status
// is still true of the row: the simulator really did write a `paid` row.
const SimulatedChip: React.FC = () => (
  <span
    className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
    title="Written by a billing-cycle simulation. No payment was taken."
  >
    <FlaskConical className="w-3 h-3" />
    Simulated
  </span>
);

const InvoiceStatusChip: React.FC<{ status: InvoiceLedgerStatus }> = ({ status }) => {
  const chip = STATUS_CHIP[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };
  return (
    <span
      className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap ${chip.className}`}
    >
      {status === 'upcoming' && <Clock className="w-3 h-3" />}
      {chip.label}
    </span>
  );
};

// Amber, not red: nothing is broken for the user and nothing is lost — we
// just can't prove the list is complete. Explicit background + border so it
// reads on both themes (`glass` would be near-transparent on the light page).
const LedgerWarning: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200">
    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
    <p className="leading-snug">{children}</p>
  </div>
);

const InvoiceRowSkeleton: React.FC = () => (
  <div className="space-y-3 py-2" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 animate-pulse">
        <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 w-20 flex-shrink-0" />
        <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 flex-1" />
        <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 w-16 flex-shrink-0" />
        <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 w-14 flex-shrink-0" />
      </div>
    ))}
  </div>
);

// The action link is provider-aware and returns null rather than a dead link:
// Stripe rows prefer the PDF, Shopify rows only ever have `hosted_url` (the
// shop-admin charges page), bundle rows may have neither.
const InvoiceActionLink: React.FC<{ entry: InvoiceLedgerEntry }> = ({ entry }) => {
  // 🚨 A simulated row carries a `hosted_url` like any other — the ledger
  // writer stamps the shop's `/admin/charges` page unconditionally — but
  // Shopify was NEVER CALLED for this charge, so it does not exist on that
  // page. Following the link is worse than a 404: on a production dev store a
  // real charges page loads and simply lacks the row, and the reader concludes
  // Shopify lost a charge. Say what happened instead of linking to nowhere.
  if (isSimulated(entry)) {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        Not in Shopify
      </span>
    );
  }
  const pdf = entry.pdf_url || null;
  const hosted = entry.hosted_url || null;
  if (!pdf && !hosted) return null;
  const href = (pdf || hosted) as string;
  // Below `sm` the table reflows to the viewport, so the label shortens to
  // "View" rather than widening the column back past the fold — the row's own
  // description already says which provider the charge came from.
  const longLabel = pdf ? 'PDF' : entry.provider === 'shopify' ? 'View in Shopify' : 'View';
  const shortLabel = pdf ? 'PDF' : 'View';
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-primary hover:underline text-xs inline-flex items-center gap-1 whitespace-nowrap"
    >
      <span className="hidden sm:inline">{longLabel}</span>
      <span className="sm:hidden">{shortLabel}</span>
      {pdf ? <FileDown className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
    </a>
  );
};

const InvoiceLedgerSection: React.FC<{
  workspaceId: string;
  /**
   * Seeds the provider-dependent copy for the render passes that happen BEFORE
   * the first response lands. The response is still the authority — this only
   * decides which provider a first-load FAILURE names, and defaulting a Shopify
   * merchant to "we couldn't load your billing history from Stripe" is a lie
   * about a system they have no account with.
   */
  defaultProvider?: 'stripe' | 'shopify';
}> = ({ workspaceId, defaultProvider = 'stripe' }) => {
  const [entries, setEntries] = useState<InvoiceLedgerEntry[]>([]);
  const [provider, setProvider] = useState<string>(defaultProvider);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  // Sticky across pages: once any page has told us it was serving a partial
  // view, "Load more" cannot silently clear the warning.
  const [degraded, setDegraded] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // `quiet` = a page-level Refresh, not a first paint. It reloads page one
  // exactly the same way, but leaves the rows on screen instead of collapsing
  // the table back to a skeleton: the Refresh button already carries the busy
  // state, and blanking the history the user is reading to re-show it a moment
  // later is the flicker, not the feedback.
  const load = async (nextCursor: string | null, opts?: { quiet?: boolean }) => {
    const first = nextCursor === null;
    if (first) {
      if (!opts?.quiet) setLoading(true);
      setFailed(false);
      setDegraded(false);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await billingAPI.listInvoices(workspaceId, { limit: 20, cursor: nextCursor });
      const data = res.data;
      setProvider(data?.provider ?? defaultProvider);
      setEntries((prev) => (first ? data?.entries ?? [] : [...prev, ...(data?.entries ?? [])]));
      setDegraded((prev) => (first ? !!data?.source_degraded : prev || !!data?.source_degraded));
      setCursor(data?.next_cursor ?? null);
      setHasMore(!!data?.has_more && !!data?.next_cursor);
    } catch {
      // 🚨 Never fall through to the empty state here. A failed request is
      // not an empty history — that is the exact bug this ship fixes.
      // Same rule one level down for a quiet refresh: a refresh that fails
      // must leave the history the user was already reading exactly where it
      // was, not replace it with an error panel.
      if (first && !opts?.quiet) {
        setEntries([]);
        setFailed(true);
      } else if (first && entries.length === 0) {
        setFailed(true);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!workspaceId) return;
    load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // 🚨 This section owns its own endpoint (`/api/billing/invoices`) — it is
  // NOT part of the billing payload, so the page's Refresh button reaches it
  // only through this registration. Refreshing collapses back to page one,
  // which is what a browser reload does too.
  useBillingRefreshSource('invoice-ledger', async () => {
    if (!workspaceId) return;
    await load(null, { quiet: true });
  });

  // §3: `provider: "internal"` ⇒ the section stays hidden, exactly as the
  // `isInternalWorkspace` guard does today.
  if (provider === 'internal' && !loading) return null;

  const providerLabel = PROVIDER_LABEL[provider] ?? 'your billing provider';

  // 🚨 We never issue an invoice DOCUMENT for a Shopify workspace — Shopify
  // bills the merchant and the charge only exists on their own admin billing
  // page (`back/api/billing_shopify.py` stores exactly that as `hosted_url`,
  // and `pdf_url` is null for every Shopify row). So the section cannot be
  // headed "Invoice History" there: that names a document we do not have and
  // sets up the merchant to go looking for a download.
  const isShopify = provider === 'shopify';

  return (
    <section className="card">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        {isShopify ? 'Billing History' : 'Invoice History'}
      </h2>

      {loading ? (
        <InvoiceRowSkeleton />
      ) : failed ? (
        <div className="space-y-3">
          <LedgerWarning>
            We couldn't load your billing history from {providerLabel}. Nothing has been lost —
            try again shortly.
          </LedgerWarning>
          <button
            onClick={() => load(null)}
            className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm inline-flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      ) : entries.length === 0 ? (
        degraded ? (
          // 🚨 Empty + degraded. NEVER the words "No invoices yet." here.
          <div className="space-y-3">
            <LedgerWarning>
              We couldn't load your billing history from {providerLabel}. Nothing has been lost —
              try again shortly.
            </LedgerWarning>
            <button
              onClick={() => load(null)}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        ) : (
          // Not degraded and genuinely empty. On Shopify this is the COMMON
          // state, not the edge case: a charge row is only written when a
          // subscription webhook arrives with an active status and a known
          // cycle end, so a merchant who just installed may sit here for a
          // full billing period. It must read as "nothing has happened yet",
          // never as "something is missing".
          <div className="py-6 text-center space-y-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isShopify ? 'No charges yet.' : 'No invoices yet.'}
            </p>
            {isShopify && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Charges appear here after Shopify bills your subscription.
              </p>
            )}
          </div>
        )
      ) : (
        <div className="space-y-3">
          {degraded && (
            <LedgerWarning>
              This list may be incomplete — we couldn't reach {providerLabel} for the most recent
              charges.
            </LedgerWarning>
          )}
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm min-w-0 sm:min-w-[500px]">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 px-2 sm:px-3">Date</th>
                  <th className="py-2 px-2 sm:px-3">Description</th>
                  <th className="py-2 px-2 sm:px-3">Amount</th>
                  {/* Below `sm` the Status chip and the action link both fold
                      into the Description cell, leaving three columns that fit
                      375px without a scroller — the amount is the payload of a
                      billing history and must not need a swipe, and an
                      "Upcoming" charge you can only find by swiping is the
                      distinction failing to land. Not duplicated: exactly one
                      of each pair renders at any given width. */}
                  <th className="py-2 px-3 hidden sm:table-cell">Status</th>
                  <th className="py-2 px-3 hidden sm:table-cell"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  // 🚨 `entry_id`, never `id`: a retried payment emits a
                  // `failed` row and a `paid` row sharing one `provider_ref`.
                  // Opaque — do not parse it (Storm `back 2df0e43`).
                  <tr key={entry.entry_id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-2 sm:px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formatDate(entry.created)}
                    </td>
                    <td className="py-3 px-2 sm:px-3 text-gray-700 dark:text-gray-300">
                      <span className="block">{entry.description || 'Charge'}</span>
                      {entry.number && (
                        <span className="block font-mono text-[11px] text-gray-500 dark:text-gray-400">
                          {entry.number}
                        </span>
                      )}
                      <span className="mt-1 flex sm:hidden items-center gap-2 flex-wrap">
                        <InvoiceStatusChip status={entry.status} />
                        {isSimulated(entry) && <SimulatedChip />}
                        <InvoiceActionLink entry={entry} />
                      </span>
                    </td>
                    {/* 🚨 A retried payment shows TWO rows for one invoice — a
                        `failed` one and a `paid` one, same invoice number, same
                        amount, both correct. Rendered, that read as "$99.00
                        twice" with the money styled identically in both. So a
                        row where no money moved states it: the figure is what
                        was DUE, de-emphasised, with "Not charged" under it. No
                        inference about a later retry — just what this row is. */}
                    <td className="py-3 px-2 sm:px-3 whitespace-nowrap">
                      {/* amount_* are MINOR units — formatCurrency divides by 100.
                          A settled row reports what actually moved; anything not
                          yet settled (upcoming/open/failed) reports what is due. */}
                      <span
                        className={
                          noMoneyMoved(entry)
                            ? 'block font-medium text-gray-500 dark:text-gray-400'
                            : 'block font-semibold text-gray-900 dark:text-white'
                        }
                      >
                        {formatCurrency(
                          entry.status === 'paid' && entry.amount_paid ? entry.amount_paid : entry.amount_due,
                          entry.currency,
                        )}
                      </span>
                      {noMoneyMoved(entry) && (
                        // 🚨 `whitespace-normal` overrides the cell's nowrap,
                        // which exists to keep the FIGURE unbroken. Without it
                        // the longer simulated label is unbreakable too, and at
                        // 375px it widens the amount column until the
                        // description wraps to five lines. Rendering caught
                        // this; reading the class list would not have.
                        <span className="block whitespace-normal text-[11px] text-gray-500 dark:text-gray-400">
                          {isSimulated(entry) ? 'Simulated — not charged' : 'Not charged'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 hidden sm:table-cell">
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <InvoiceStatusChip status={entry.status} />
                        {isSimulated(entry) && <SimulatedChip />}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right hidden sm:table-cell">
                      <InvoiceActionLink entry={entry} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="pt-1 flex justify-center">
              <button
                onClick={() => load(cursor)}
                disabled={loadingMore}
                className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {/* 🚨 Shopify only, and it stays visible in the empty state too. The
          merchant's money is taken by Shopify on our behalf; there is no
          document on our side to download, now or later. Saying so here is
          what stops the missing PDF column from reading as a bug. */}
      {isShopify && !loading && (
        <p className="mt-4 flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Shopify bills and collects these charges, so there's no invoice to
            download from us
            {entries.length > 0
              ? ' — the links open the charge on your Shopify admin billing page.'
              : '. Your Shopify admin billing page has the full record.'}
          </span>
        </p>
      )}
    </section>
  );
};

export default InvoiceLedgerSection;
